:host {
  /**
   * @prop --background: Background of the item
   * @prop --background-activated: Background of the item when pressed. Note: setting this will interfere with the Material Design ripple.
   * @prop --background-activated-opacity: Opacity of the item background when pressed
   * @prop --background-focused: Background of the item when focused with the tab key
   * @prop --background-focused-opacity: Opacity of the item background when focused with the tab key
   * @prop --background-hover: Background of the item on hover
   * @prop --background-hover-opacity: Opacity of the background of the item on hover
   *
   * @prop --border-color: Color of the item border
   * @prop --border-radius: Radius of the item border
   * @prop --border-style: Style of the item border
   * @prop --border-width: Width of the item border
   *
   * @prop --color: Color of the item
   * @prop --color-activated: Color of the item when pressed
   * @prop --color-focused: Color of the item when focused with the tab key
   * @prop --color-hover: Color of the item on hover
   *
   * @prop --detail-icon-color: Color of the item detail icon
   * @prop --detail-icon-opacity: Opacity of the item detail icon
   * @prop --detail-icon-font-size: Font size of the item detail icon
   *
   * @prop --inner-border-width: Width of the item inner border
   * @prop --inner-box-shadow: Box shadow of the item inner
   * @prop --inner-padding-top: Top padding of the item inner
   * @prop --inner-padding-end: Right padding if direction is left-to-right, and left padding if direction is right-to-left of the item inner
   * @prop --inner-padding-bottom: Bottom padding of the item inner
   * @prop --inner-padding-start: Left padding if direction is left-to-right, and right padding if direction is right-to-left of the item inner
   *
   * @prop --min-height: Minimum height of the item
   *
   * @prop --padding-bottom: Bottom padding of the item
   * @prop --padding-end: Right padding if direction is left-to-right, and left padding if direction is right-to-left of the item
   * @prop --padding-start: Left padding if direction is left-to-right, and right padding if direction is right-to-left of the item
   * @prop --padding-top: Top padding of the item
   *
   * @prop --transition: Transition of the item
   *
   * @prop --ripple-color: Color of the item ripple effect
   *
   * @prop --highlight-height: The height of the highlight on the item
   * @prop --highlight-color-focused: The color of the highlight on the item when focused
   * @prop --highlight-color-valid: The color of the highlight on the item when valid
   * @prop --highlight-color-invalid: The color of the highlight on the item when invalid
   */
  --border-radius: 0px;
  --border-width: 0px;
  --border-style: solid;
  --padding-top: 0px;
  --padding-bottom: 0px;
  --padding-end: 0px;
  --padding-start: 0px;
  --inner-border-width: 0px;
  --inner-padding-top: 0px;
  --inner-padding-bottom: 0px;
  --inner-padding-start: 0px;
  --inner-padding-end: 0px;
  --inner-box-shadow: none;
  --show-full-highlight: 0;
  --show-inset-highlight: 0;
  --detail-icon-color: initial;
  --detail-icon-font-size: 20px;
  --detail-icon-opacity: 0.25;
  --color-activated: var(--color);
  --color-focused: var(--color);
  --color-hover: var(--color);
  --ripple-color: currentColor;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  display: block;
  position: relative;
  align-items: center;
  justify-content: space-between;
  outline: none;
  color: var(--color);
  font-family: var(--ion-font-family, inherit);
  text-align: initial;
  text-decoration: none;
  overflow: hidden;
  box-sizing: border-box;
}

:host(.ion-color:not(.item-fill-solid):not(.item-fill-outline)) .item-native {
  background: var(--ion-color-base);
  color: var(--ion-color-contrast);
}

:host(.ion-color:not(.item-fill-solid):not(.item-fill-outline)) .item-native,
:host(.ion-color:not(.item-fill-solid):not(.item-fill-outline)) .item-inner {
  border-color: var(--ion-color-shade);
}

:host(.ion-activated) .item-native {
  color: var(--color-activated);
}
:host(.ion-activated) .item-native::after {
  background: var(--background-activated);
  opacity: var(--background-activated-opacity);
}

:host(.ion-color.ion-activated) .item-native {
  color: var(--ion-color-contrast);
}

:host(.ion-focused) .item-native {
  color: var(--color-focused);
}
:host(.ion-focused) .item-native::after {
  background: var(--background-focused);
  opacity: var(--background-focused-opacity);
}

:host(.ion-color.ion-focused) .item-native {
  color: var(--ion-color-contrast);
}
:host(.ion-color.ion-focused) .item-native::after {
  background: var(--ion-color-contrast);
}

@media (any-hover: hover) {
  :host(.ion-activatable:not(.ion-focused):hover) .item-native {
    color: var(--color-hover);
  }
  :host(.ion-activatable:not(.ion-focused):hover) .item-native::after {
    background: var(--background-hover);
    opacity: var(--background-hover-opacity);
  }
  :host(.ion-color.ion-activatable:not(.ion-focused):hover) .item-native {
    color: var(--ion-color-contrast);
  }
  :host(.ion-color.ion-activatable:not(.ion-focused):hover) .item-native::after {
    background: var(--ion-color-contrast);
  }
}
:host(.item-interactive-disabled:not(.item-multiple-inputs)) {
  cursor: default;
  pointer-events: none;
}

:host(.item-disabled) {
  cursor: default;
  opacity: 0.3;
  pointer-events: none;
}

.item-native {
  border-radius: var(--border-radius);
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  -webkit-padding-start: calc(var(--padding-start) + var(--ion-safe-area-left, 0px));
  padding-inline-start: calc(var(--padding-start) + var(--ion-safe-area-left, 0px));
  -webkit-padding-end: var(--padding-end);
  padding-inline-end: var(--padding-end);
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  font-family: inherit;
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  text-decoration: inherit;
  text-indent: inherit;
  text-overflow: inherit;
  text-transform: inherit;
  text-align: inherit;
  white-space: inherit;
  color: inherit;
  display: flex;
  position: relative;
  align-items: inherit;
  justify-content: inherit;
  width: 100%;
  min-height: var(--min-height);
  transition: var(--transition);
  border-width: var(--border-width);
  border-style: var(--border-style);
  border-color: var(--border-color);
  outline: none;
  background: var(--background);
  overflow: inherit;
  box-sizing: border-box;
  z-index: 1;
}

.item-native::-moz-focus-inner {
  border: 0;
}

.item-native::after {
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  position: absolute;
  content: "";
  opacity: 0;
  transition: var(--transition);
  z-index: -1;
}

button, a {
  cursor: pointer;
  user-select: none;
  -webkit-user-drag: none;
}

.item-inner {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  -webkit-padding-start: var(--inner-padding-start);
  padding-inline-start: var(--inner-padding-start);
  -webkit-padding-end: calc(var(--ion-safe-area-right, 0px) + var(--inner-padding-end));
  padding-inline-end: calc(var(--ion-safe-area-right, 0px) + var(--inner-padding-end));
  padding-top: var(--inner-padding-top);
  padding-bottom: var(--inner-padding-bottom);
  display: flex;
  position: relative;
  flex: 1;
  flex-direction: inherit;
  align-items: inherit;
  align-self: stretch;
  min-height: inherit;
  border-width: var(--inner-border-width);
  border-style: var(--border-style);
  border-color: var(--border-color);
  box-shadow: var(--inner-box-shadow);
  overflow: inherit;
  box-sizing: border-box;
}

.item-bottom {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  -webkit-padding-start: calc(var(--padding-start) + var(--ion-safe-area-left, 0px));
  padding-inline-start: calc(var(--padding-start) + var(--ion-safe-area-left, 0px));
  -webkit-padding-end: var(--inner-padding-end);
  padding-inline-end: var(--inner-padding-end);
  padding-top: 0;
  padding-bottom: 0;
  display: flex;
  justify-content: space-between;
}

.item-detail-icon {
  /**
   * IonItem applies its own end padding. However,
   * all IonIcons have the same bounding box meaning
   * there may be white space for narrow icons. As
   * a result, the white space gives the appearance
   * that there is too much end margin. We move the default
   * chevron icon over to account for this extra whitespace.
   */
  -webkit-margin-start: calc(var(--inner-padding-end) / 2);
  margin-inline-start: calc(var(--inner-padding-end) / 2);
  -webkit-margin-end: -6px;
  margin-inline-end: -6px;
  color: var(--detail-icon-color);
  font-size: var(--detail-icon-font-size);
  opacity: var(--detail-icon-opacity);
}

::slotted(ion-icon) {
  font-size: 1.6em;
}

::slotted(ion-button) {
  --margin-top: 0;
  --margin-bottom: 0;
  --margin-start: 0;
  --margin-end: 0;
  z-index: 1;
}

::slotted(ion-label:not([slot=end])) {
  flex: 1;
}

:host(.item-input) {
  align-items: center;
}

.input-wrapper {
  display: flex;
  flex: 1;
  flex-direction: inherit;
  align-items: inherit;
  align-self: stretch;
  text-overflow: ellipsis;
  overflow: inherit;
  box-sizing: border-box;
}

:host(.item-label-stacked),
:host(.item-label-floating) {
  align-items: start;
}

:host(.item-label-stacked) .input-wrapper,
:host(.item-label-floating) .input-wrapper {
  flex: 1;
  flex-direction: column;
}

.item-highlight,
.item-inner-highlight {
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  border-radius: inherit;
  position: absolute;
  width: 100%;
  height: 100%;
  transform: scaleX(0);
  transition: transform 200ms, border-bottom-width 200ms;
  z-index: 2;
  box-sizing: border-box;
  pointer-events: none;
}

:host(.item-interactive.ion-focused),
:host(.item-interactive.item-has-focus),
:host(.item-interactive.ion-touched.ion-invalid) {
  --full-highlight-height: calc(var(--highlight-height) * var(--show-full-highlight));
  --inset-highlight-height: calc(var(--highlight-height) * var(--show-inset-highlight));
}

:host(.ion-focused) .item-highlight,
:host(.ion-focused) .item-inner-highlight,
:host(.item-has-focus) .item-highlight,
:host(.item-has-focus) .item-inner-highlight {
  transform: scaleX(1);
  border-style: var(--border-style);
  border-color: var(--highlight-background);
}

:host(.ion-focused) .item-highlight,
:host(.item-has-focus) .item-highlight {
  border-width: var(--full-highlight-height);
  opacity: var(--show-full-highlight);
}

:host(.ion-focused) .item-inner-highlight,
:host(.item-has-focus) .item-inner-highlight {
  border-bottom-width: var(--inset-highlight-height);
  opacity: var(--show-inset-highlight);
}

:host(.ion-focused.item-fill-solid) .item-highlight,
:host(.item-has-focus.item-fill-solid) .item-highlight {
  border-width: calc(var(--full-highlight-height) - 1px);
}

:host(.ion-focused) .item-inner-highlight,
:host(.ion-focused:not(.item-fill-outline)) .item-highlight,
:host(.item-has-focus) .item-inner-highlight,
:host(.item-has-focus:not(.item-fill-outline)) .item-highlight {
  border-top: none;
  border-right: none;
  border-left: none;
}

:host(.item-interactive.ion-focused),
:host(.item-interactive.item-has-focus) {
  --highlight-background: var(--highlight-color-focused);
}

:host(.item-interactive.ion-valid) {
  --highlight-background: var(--highlight-color-valid);
}

:host(.item-interactive.ion-invalid) {
  --highlight-background: var(--highlight-color-invalid);
}

:host(.item-interactive.ion-invalid) ::slotted([slot=helper]) {
  display: none;
}

::slotted([slot=error]) {
  display: none;
  color: var(--highlight-color-invalid);
}

:host(.item-interactive.ion-invalid) ::slotted([slot=error]) {
  display: block;
}

:host(:not(.item-label)) ::slotted(ion-select.legacy-select) {
  --padding-start: 0;
  max-width: none;
}

:host(.item-label-stacked) ::slotted(ion-select.legacy-select),
:host(.item-label-floating) ::slotted(ion-select.legacy-select) {
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 0;
  align-self: stretch;
  width: 100%;
  max-width: 100%;
}

:host(:not(.item-label)) ::slotted(ion-datetime) {
  --padding-start: 0;
}

:host(.item-label-stacked) ::slotted(ion-datetime),
:host(.item-label-floating) ::slotted(ion-datetime) {
  --padding-start: 0;
  width: 100%;
}

:host(.item-multiple-inputs) ::slotted(ion-checkbox),
:host(.item-multiple-inputs) ::slotted(ion-datetime),
:host(.item-multiple-inputs) ::slotted(ion-radio),
:host(.item-multiple-inputs) ::slotted(ion-select.legacy-select) {
  position: relative;
}

:host(.item-textarea) {
  align-items: stretch;
}

::slotted(ion-reorder[slot]) {
  margin-top: 0;
  margin-bottom: 0;
}

ion-ripple-effect {
  color: var(--ripple-color);
}

:host(.item-fill-solid) ::slotted([slot=start]),
:host(.item-fill-solid) ::slotted([slot=end]),
:host(.item-fill-outline) ::slotted([slot=start]),
:host(.item-fill-outline) ::slotted([slot=end]) {
  align-self: center;
}

::slotted([slot=helper]),
::slotted([slot=error]),
.item-counter {
  padding-top: 5px;
  font-size: 12px;
  z-index: 1;
}

.item-counter {
  -webkit-margin-start: auto;
  margin-inline-start: auto;
  color: var(--ion-color-step-550, #737373);
  white-space: nowrap;
  padding-inline-start: 16px;
}

@media (prefers-reduced-motion: reduce) {
  .item-highlight,
  .item-inner-highlight {
    transition: none;
  }
}
:host {
  --min-height: 48px;
  --background: var(--ion-item-background, var(--ion-background-color, #fff));
  --background-activated: transparent;
  --background-focused: currentColor;
  --background-hover: currentColor;
  --background-activated-opacity: 0;
  --background-focused-opacity: .12;
  --background-hover-opacity: .04;
  --border-color: var(--ion-item-border-color, var(--ion-border-color, var(--ion-color-step-150, rgba(0, 0, 0, 0.13))));
  --color: var(--ion-item-color, var(--ion-text-color, #000));
  --transition: opacity 15ms linear, background-color 15ms linear;
  --padding-start: 16px;
  --inner-padding-end: 16px;
  --inner-border-width: 0 0 1px 0;
  --highlight-height: 1px;
  --highlight-color-focused: var(--ion-color-primary, #3880ff);
  --highlight-color-valid: var(--ion-color-success, #2dd36f);
  --highlight-color-invalid: var(--ion-color-danger, #eb445a);
  font-size: 16px;
  font-weight: normal;
  text-transform: none;
}

:host(.item-fill-outline) {
  --highlight-height: 2px;
}

:host(.item-fill-none.item-interactive.ion-focus) .item-highlight,
:host(.item-fill-none.item-interactive.item-has-focus) .item-highlight,
:host(.item-fill-none.item-interactive.ion-touched.ion-invalid) .item-highlight {
  transform: scaleX(1);
  border-width: 0 0 var(--full-highlight-height) 0;
  border-style: var(--border-style);
  border-color: var(--highlight-background);
}

:host(.item-fill-none.item-interactive.ion-focus) .item-native,
:host(.item-fill-none.item-interactive.item-has-focus) .item-native,
:host(.item-fill-none.item-interactive.ion-touched.ion-invalid) .item-native {
  border-bottom-color: var(--highlight-background);
}

:host(.item-fill-outline.item-interactive.ion-focus) .item-highlight,
:host(.item-fill-outline.item-interactive.item-has-focus) .item-highlight {
  transform: scaleX(1);
}

:host(.item-fill-outline.item-interactive.ion-focus) .item-highlight,
:host(.item-fill-outline.item-interactive.item-has-focus) .item-highlight,
:host(.item-fill-outline.item-interactive.ion-touched.ion-invalid) .item-highlight {
  border-width: var(--full-highlight-height);
  border-style: var(--border-style);
  border-color: var(--highlight-background);
}

:host(.item-fill-outline.item-interactive.ion-touched.ion-invalid) .item-native {
  border-color: var(--highlight-background);
}

:host(.item-fill-solid.item-interactive.ion-focus) .item-highlight,
:host(.item-fill-solid.item-interactive.item-has-focus) .item-highlight,
:host(.item-fill-solid.item-interactive.ion-touched.ion-invalid) .item-highlight {
  transform: scaleX(1);
  border-width: 0 0 var(--full-highlight-height) 0;
  border-style: var(--border-style);
  border-color: var(--highlight-background);
}

:host(.item-fill-solid.item-interactive.ion-focus) .item-native,
:host(.item-fill-solid.item-interactive.item-has-focus) .item-native,
:host(.item-fill-solid.item-interactive.ion-touched.ion-invalid) .item-native {
  border-bottom-color: var(--highlight-background);
}

:host(.ion-color.ion-activated) .item-native::after {
  background: transparent;
}

:host(.item-has-focus) .item-native {
  caret-color: var(--highlight-background);
}

:host(.item-interactive) {
  --border-width: 0 0 1px 0;
  --inner-border-width: 0;
  --show-full-highlight: 1;
  --show-inset-highlight: 0;
}

:host(.item-lines-full) {
  --border-width: 0 0 1px 0;
  --show-full-highlight: 1;
  --show-inset-highlight: 0;
}

:host(.item-lines-inset) {
  --inner-border-width: 0 0 1px 0;
  --show-full-highlight: 0;
  --show-inset-highlight: 1;
}

:host(.item-lines-inset),
:host(.item-lines-none) {
  --border-width: 0;
  --show-full-highlight: 0;
}

:host(.item-lines-full),
:host(.item-lines-none) {
  --inner-border-width: 0;
  --show-inset-highlight: 0;
}

/**
 * When `fill="outline"`, reposition the highlight element to cover everything but the `.item-bottom`
 */
:host(.item-fill-outline) .item-highlight {
  --position-offset: calc(-1 * var(--border-width));
  top: var(--position-offset);
  width: calc(100% + 2 * var(--border-width));
  height: calc(100% + 2 * var(--border-width));
  transition: none;
}
@supports (inset-inline-start: 0) {
  :host(.item-fill-outline) .item-highlight {
    inset-inline-start: var(--position-offset);
  }
}
@supports not (inset-inline-start: 0) {
  :host(.item-fill-outline) .item-highlight {
    left: var(--position-offset);
  }
  :host-context([dir=rtl]):host(.item-fill-outline) .item-highlight, :host-context([dir=rtl]).item-fill-outline .item-highlight {
    left: unset;
    right: unset;
    right: var(--position-offset);
  }
  @supports selector(:dir(rtl)) {
    :host(.item-fill-outline) .item-highlight:dir(rtl) {
      left: unset;
      right: unset;
      right: var(--position-offset);
    }
  }
}

:host(.item-fill-outline.ion-focused) .item-native,
:host(.item-fill-outline.item-has-focus) .item-native {
  border-color: transparent;
}

:host(.item-multi-line) ::slotted([slot=start]),
:host(.item-multi-line) ::slotted([slot=end]) {
  margin-top: 16px;
  margin-bottom: 16px;
  align-self: flex-start;
}

::slotted([slot=start]) {
  -webkit-margin-end: 32px;
  margin-inline-end: 32px;
}

::slotted([slot=end]) {
  -webkit-margin-start: 32px;
  margin-inline-start: 32px;
}

:host(.item-fill-solid) ::slotted([slot=start]),
:host(.item-fill-solid) ::slotted([slot=end]),
:host(.item-fill-outline) ::slotted([slot=start]),
:host(.item-fill-outline) ::slotted([slot=end]) {
  align-self: center;
}

::slotted(ion-icon) {
  color: rgba(var(--ion-text-color-rgb, 0, 0, 0), 0.54);
  font-size: 24px;
}

:host(.ion-color:not(.item-fill-solid):not(.item-fill-outline)) ::slotted(ion-icon) {
  color: var(--ion-color-contrast);
}

::slotted(ion-icon[slot]) {
  margin-top: 12px;
  margin-bottom: 12px;
}

::slotted(ion-icon[slot=start]) {
  -webkit-margin-end: 32px;
  margin-inline-end: 32px;
}

::slotted(ion-icon[slot=end]) {
  -webkit-margin-start: 16px;
  margin-inline-start: 16px;
}

:host(.item-fill-solid) ::slotted(ion-icon[slot=start]),
:host(.item-fill-outline) ::slotted(ion-icon[slot=start]) {
  -webkit-margin-end: 8px;
  margin-inline-end: 8px;
}

::slotted(ion-toggle[slot=start]),
::slotted(ion-toggle[slot=end]) {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
}

::slotted(ion-note) {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  align-self: flex-start;
  font-size: 11px;
}

::slotted(ion-note[slot]:not([slot=helper]):not([slot=error])) {
  padding-left: 0;
  padding-right: 0;
  padding-top: 18px;
  padding-bottom: 10px;
}

::slotted(ion-note[slot=start]) {
  -webkit-padding-end: 16px;
  padding-inline-end: 16px;
}

::slotted(ion-note[slot=end]) {
  -webkit-padding-start: 16px;
  padding-inline-start: 16px;
}

::slotted(ion-avatar) {
  width: 40px;
  height: 40px;
}

::slotted(ion-thumbnail) {
  --size: 56px;
}

::slotted(ion-avatar),
::slotted(ion-thumbnail) {
  margin-top: 8px;
  margin-bottom: 8px;
}

::slotted(ion-avatar[slot=start]),
::slotted(ion-thumbnail[slot=start]) {
  -webkit-margin-end: 16px;
  margin-inline-end: 16px;
}

::slotted(ion-avatar[slot=end]),
::slotted(ion-thumbnail[slot=end]) {
  -webkit-margin-start: 16px;
  margin-inline-start: 16px;
}

::slotted(ion-label) {
  margin-left: 0;
  margin-right: 0;
  margin-top: 10px;
  margin-bottom: 10px;
}

:host(.item-label-stacked) ::slotted([slot=end]),
:host(.item-label-floating) ::slotted([slot=end]) {
  margin-top: 7px;
  margin-bottom: 7px;
}

:host(.item-label-fixed) ::slotted(ion-select.legacy-select),
:host(.item-label-fixed) ::slotted(ion-datetime) {
  --padding-start: 8px;
}

:host(.item-toggle) ::slotted(ion-label),
:host(.item-radio) ::slotted(ion-label) {
  -webkit-margin-start: 0;
  margin-inline-start: 0;
}

::slotted(.button-small) {
  --padding-top: 2px;
  --padding-bottom: 2px;
  --padding-start: .6em;
  --padding-end: .6em;
  min-height: 25px;
  font-size: 12px;
}

:host(.item-label-floating),
:host(.item-label-stacked) {
  --min-height: 55px;
}

:host(.item-label-stacked) ::slotted(ion-select.legacy-select),
:host(.item-label-floating) ::slotted(ion-select.legacy-select) {
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 0;
}

:host(.ion-focused:not(.ion-color)) ::slotted(.label-stacked),
:host(.ion-focused:not(.ion-color)) ::slotted(.label-floating),
:host(.item-has-focus:not(.ion-color)) ::slotted(.label-stacked),
:host(.item-has-focus:not(.ion-color)) ::slotted(.label-floating) {
  color: var(--ion-color-primary, #3880ff);
}

:host(.ion-color) {
  --highlight-color-focused: var(--ion-color-contrast);
}

:host(.item-label-color) {
  --highlight-color-focused: var(--ion-color-base);
}

:host(.item-fill-solid.ion-color),
:host(.item-fill-outline.ion-color) {
  --highlight-color-focused: var(--ion-color-base);
}

:host(.item-fill-solid) {
  --background: var(--ion-color-step-50, #f2f2f2);
  --background-hover: var(--ion-color-step-100, #e6e6e6);
  --background-focused: var(--ion-color-step-150, #d9d9d9);
  --border-width: 0 0 1px 0;
  --inner-border-width: 0;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}
:host-context([dir=rtl]):host(.item-fill-solid), :host-context([dir=rtl]).item-fill-solid {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

@supports selector(:dir(rtl)) {
  :host(.item-fill-solid):dir(rtl) {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }
}

:host(.item-fill-solid) .item-native {
  --border-color: var(--ion-color-step-500, gray);
}

:host(.item-fill-solid.ion-focused) .item-native,
:host(.item-fill-solid.item-has-focus) .item-native {
  --background: var(--background-focused);
}

:host(.item-fill-solid.item-shape-round) {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}
:host-context([dir=rtl]):host(.item-fill-solid.item-shape-round), :host-context([dir=rtl]).item-fill-solid.item-shape-round {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

@supports selector(:dir(rtl)) {
  :host(.item-fill-solid.item-shape-round):dir(rtl) {
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }
}

@media (any-hover: hover) {
  :host(.item-fill-solid:hover) .item-native {
    --background: var(--background-hover);
    --border-color: var(--ion-color-step-750, #404040);
  }
}
:host(.item-fill-outline) {
  --ripple-color: transparent;
  --background-focused: transparent;
  --background-hover: transparent;
  --border-color: var(--ion-color-step-500, gray);
  --border-width: 1px;
  border: none;
  overflow: visible;
}

:host(.item-fill-outline) .item-native {
  --native-padding-left: 16px;
  border-radius: 4px;
}

:host(.item-fill-outline.item-shape-round) .item-native {
  --inner-padding-start: 16px;
  border-radius: 28px;
}

:host(.item-fill-outline.item-shape-round) .item-bottom {
  -webkit-padding-start: 32px;
  padding-inline-start: 32px;
}

:host(.item-fill-outline.item-label-floating.ion-focused) .item-native ::slotted(ion-input:not(:first-child)),
:host(.item-fill-outline.item-label-floating.ion-focused) .item-native ::slotted(ion-textarea:not(:first-child)),
:host(.item-fill-outline.item-label-floating.item-has-focus) .item-native ::slotted(ion-input:not(:first-child)),
:host(.item-fill-outline.item-label-floating.item-has-focus) .item-native ::slotted(ion-textarea:not(:first-child)),
:host(.item-fill-outline.item-label-floating.item-has-value) .item-native ::slotted(ion-input:not(:first-child)),
:host(.item-fill-outline.item-label-floating.item-has-value) .item-native ::slotted(ion-textarea:not(:first-child)) {
  transform: translateY(-14px);
}

@media (any-hover: hover) {
  :host(.item-fill-outline:hover) .item-native {
    --border-color: var(--ion-color-step-750, #404040);
  }
}
.item-counter {
  letter-spacing: 0.0333333333em;
}